import React, { useEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { App } from '@capacitor/app';
import { UserState, userStore } from '../api/AuthService';
import { loadClaimsPageData } from '../utils/ClaimUtils';

const CLAIMS_PATH = '/claims';
const ROUTER_SETTLE_MS = 500;

const ClaimPhotoRedirect: React.FC = () => {
    const history = useHistory();
    const location = useLocation();
    const historyRef = useRef(history);
    historyRef.current = history;

    const { driver, claimRedirectPending } = userStore((state: UserState) => ({
        driver: state.driver,
        claimRedirectPending: state.claimRedirectPending,
    }));

    const pathnameRef = useRef(location.pathname);
    pathnameRef.current = location.pathname;

    const checkStartedRef = useRef(false);

    useEffect(() => {
        if (!claimRedirectPending) {
            checkStartedRef.current = false;
        }
    }, [claimRedirectPending]);

    useEffect(() => {
        if (!claimRedirectPending || !driver?.unit || checkStartedRef.current) {
            return;
        }

        checkStartedRef.current = true;
        const unit = driver.unit;
        const driverCode = driver.driverCode;

        const runCheck = async () => {
            try {
                const { photoSubmissionRequired } = await loadClaimsPageData(
                    'claimRedirect',
                    unit,
                    driverCode,
                );

                if (!photoSubmissionRequired || pathnameRef.current.toLowerCase() === CLAIMS_PATH) {
                    return;
                }

                window.setTimeout(() => {
                    historyRef.current.push(CLAIMS_PATH);
                }, ROUTER_SETTLE_MS);
            } catch (error) {
                console.log('[ClaimsPhoto] claim redirect check error', error);
            } finally {
                userStore.setState({ claimRedirectPending: false });
            }
        };

        runCheck();
    }, [claimRedirectPending, driver?.unit, driver?.driverCode]);

    useEffect(() => {
        const appStateListener = App.addListener('appStateChange', ({ isActive }) => {
            const { isLoggedIn, driver: activeDriver, claimRedirectPending: pending } = userStore.getState();
            if (isActive && isLoggedIn && activeDriver?.unit && !pending) {
                userStore.setState({ claimRedirectPending: true });
            }
        });

        return () => {
            appStateListener.then(listener => listener.remove());
        };
    }, []);

    return null;
};

export default ClaimPhotoRedirect;
